import { ChangeEvent } from "react";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiDetailFields } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
  ROUNDING_BEHAVIOR,
} from "@miplatform/constants";
import { toastService } from "@miplatform/toast";
import { getUpdatedFields } from "@miplatform/utils";
import { mdiMinus, mdiPlus } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { IPriceLevelPayload } from "../PricingMatrix.types";

const isSameName = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

export function PriceLevelForm({
  open,
  form,
  isEditing,
  priceLevels,
  setActivePriceLevelIndex,
  handleClosePriceLevelForm,
  handleCreatePriceLevel,
  handleUpdatePriceLevel,
}: PriceLevelFormProps) {
  const {
    control,
    setValue,
    watch,
    getValues,
    formState: { isValid, isDirty, dirtyFields },
  } = form;
  const { classes, theme } = useStyles();
  const { roundingBehavior, percentAdjustment, parentId } = watch();

  if (!open) {
    return null;
  }

  const handlePriceLevelPercentage = (adjustment: number) => {
    const newPercent = percentAdjustment + adjustment;
    setValue("percentAdjustment", newPercent);
  };

  const handleSavePriceLevel = () => {
    const data = getValues();
    const alreadyAddedName = priceLevels.some(
      (pl) =>
        isSameName(pl.name, data.name) && (!isEditing || pl.id !== data.id)
    );
    if (alreadyAddedName) {
      toastService.error({
        text: `Price Level Name ${data.name} already exists.`,
      });
      return;
    }
    if (isEditing) {
      const updatedData = getUpdatedFields(dirtyFields, data);
      handleUpdatePriceLevel({ ...updatedData, id: data.id, name: data.name });
      handleClosePriceLevelForm();
    } else {
      handleCreatePriceLevel(data);
      handleClosePriceLevelForm();
      setActivePriceLevelIndex(priceLevels.length);
    }
  };

  const priceLevelFields = [
    {
      label: "Pricing Level Name",
      name: "name",
      formControl: control,
      width: "50%",
      required: true,
      InputProps: {
        inputProps: {
          maxLength: 64,
        },
      },
    },
    {
      label: "Based On Price Level",
      width: "40%",
      fieldType: INPUT_TYPES.SELECT,
      name: "parentId",
      options: priceLevels
        .filter((pl) => !pl.parentId)
        .map((pl) => ({ label: pl.name, value: pl.id })),
      formControl: control,
      disabled: priceLevels.length === 0 || isEditing,
    },
    {
      label: "Diff %",
      fieldType: "numeric",
      formControl: control,
      name: "percentAdjustment",
      width: "10%",
      flexGrow: 0,
      suffix: "%",
      decimalScale: 1,
      disabled: !parentId || isEditing,
      value: percentAdjustment,
      setValue: (value: number) => {
        setValue(`percentAdjustment`, value, { shouldDirty: true });
      },
      sx: {
        "& .MuiInputBase-root": {
          paddingRight: 0,
          paddingLeft: 0,
          justifyContent: "center",
        },
        "& .MuiInputBase-input.MuiOutlinedInput-input": {
          textAlign: "center",
          width: 100,
        },
      },
      InputProps: {
        startAdornment: (
          <MiButton
            startIcon={{ path: mdiMinus }}
            size="small"
            className={classes.zeroPadding}
            onClick={() => handlePriceLevelPercentage(-0.1)}
            disabled={!parentId || isEditing}
          />
        ),
        endAdornment: (
          <MiButton
            startIcon={{ path: mdiPlus }}
            size="small"
            className={classes.zeroPadding}
            onClick={() => handlePriceLevelPercentage(0.1)}
            disabled={!parentId || isEditing}
          />
        ),
      },
    },
    {
      fieldType: INPUT_TYPES.SWITCH,
      label: "Round down price",
      name: "roundingBehavior",
      formControl: control,
      width: "12%",
      flexGrow: 0,
      disabled: !percentAdjustment || isEditing,
      backgroundColor: theme.palette.grey.light,
      value: roundingBehavior === ROUNDING_BEHAVIOR.DOWN,
      setValue: (e: ChangeEvent<HTMLInputElement>) => {
        setValue(
          "roundingBehavior",
          e.target.checked ? ROUNDING_BEHAVIOR.DOWN : ROUNDING_BEHAVIOR.NONE
        );
      },
    },
    {
      fieldType: INPUT_TYPES.SWITCH,
      label: "Round up price",
      name: "roundingBehavior",
      formControl: control,
      width: "12%",
      flexGrow: 0,
      disabled: !percentAdjustment || isEditing,
      backgroundColor: theme.palette.grey.light,
      value: roundingBehavior === ROUNDING_BEHAVIOR.UP,
      setValue: (e: ChangeEvent<HTMLInputElement>) => {
        setValue(
          "roundingBehavior",
          e.target.checked ? ROUNDING_BEHAVIOR.UP : ROUNDING_BEHAVIOR.NONE
        );
      },
    },
    {
      fieldType: INPUT_TYPES.CUSTOM,
      customComponent: (
        <Box className={classes.buttonContainer}>
          <MiButton
            title="Cancel"
            variant={BUTTON_VARIANTS.TEXT}
            className={classes.cancelButton}
            color={BUTTON_COLORS.BLACK}
            onClick={handleClosePriceLevelForm}
          />
          <MiButton
            title="Save"
            variant={BUTTON_VARIANTS.CONTAINED}
            disabled={!isValid || !isDirty}
            onClick={handleSavePriceLevel}
          />
        </Box>
      ),
    } as any,
  ];

  return (
    <Box className={classes.formContainer}>
      <Typography variant="body2" className={classes.title}>
        {isEditing ? "Edit Price Level" : " Add New Price Level"}
      </Typography>
      <MiDetailFields detailFields={priceLevelFields} />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  zeroPadding: {
    padding: 0,
  },
  cancelButton: {
    textDecoration: "underline",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(4),
    backgroundColor: theme.palette.grey.light,
  },
  formContainer: {
    backgroundColor: theme.palette.grey.light,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  title: {
    paddingLeft: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    fontWeight: theme.fontWeight.semiBold,
  },
}));

interface PriceLevelFormProps {
  open: boolean;
  form: UseFormReturn<IPriceLevelPayload>;
  priceLevels: IPriceLevelPayload[];
  isEditing: boolean;
  setActivePriceLevelIndex: React.Dispatch<React.SetStateAction<number | null>>;
  handleClosePriceLevelForm: () => void;
  handleCreatePriceLevel: (data: IPriceLevelPayload) => void;
  handleUpdatePriceLevel: (data: Partial<IPriceLevelPayload>) => void;
}
